import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

/** Semantic button tones mapped to `.btn--*` classes in theme.css. */
export type ButtonTone = 'primary' | 'secondary' | 'ghostOnDark' | 'link' | 'danger';

const TONE_CLASS: Record<ButtonTone, string> = {
  primary: 'btn btn--primary',
  secondary: 'btn btn--secondary',
  ghostOnDark: 'btn btn--ghost-on-dark',
  link: 'btn btn--link',
  danger: 'btn btn--danger',
};

export type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  children: ReactNode;
  /** Maps to `.btn--*` utility classes in `@nrx/ui/theme.css`. */
  tone?: ButtonTone;
  /**
   * Marks an in-flight action: forces `disabled` and sets `aria-busy`.
   * Callers own pending label text via `children`.
   */
  busy?: boolean;
};

/** Standard button for @nrx platform apps. Requires `@import '@nrx/ui/theme.css'`. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { children, className, tone = 'primary', type = 'button', busy = false, disabled, ...props },
  ref,
): React.JSX.Element {
  const classes = [TONE_CLASS[tone], className].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      {...props}
      disabled={Boolean(disabled || busy)}
      aria-busy={busy || undefined}
    >
      {children}
    </button>
  );
});
